// pages/Accounts.jsx
import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  RefreshCw,
  Building2,
  Phone,
  Globe,
  MapPin,
  TrendingUp,
  Users,
  ChevronDown,
} from "lucide-react";
import { Add } from "iconsax-reactjs";
import apiService from "../../services/api";
import { useNavigate } from "react-router-dom";
import CreateAccountModal from "../../components/CreateAccountModal";
import AccountMergeManager from "../../components/AccountMergeManager";
import { useTheme } from "../../contexts/ThemeContext";

const Accounts = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({
    totalAccounts: 0,
    accountsByType: {},
    accountsByIndustry: {},
    recentAccounts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    type: "",
    industry: "",
    rating: "",
    search: "",
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMergeManager, setShowMergeManager] = useState(false);

  useEffect(() => {
    fetchAccounts();
    fetchStats();
  }, [pagination.page, filters]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccounts({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });

      setAccounts(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getAccountStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleCreateSuccess = () => {
    fetchAccounts();
    fetchStats();
    setShowCreateModal(false);
  };

  const getTypeColor = (type) => {
    const colors = {
      Prospect: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      "Customer - Direct": "bg-green-500/10 text-green-400 border-green-500/20",
      "Customer - Channel": "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
      "Channel Partner / Reseller":
        "bg-purple-500/10 text-purple-400 border-purple-500/20",
      "Installation Partner":
        "bg-orange-500/10 text-orange-400 border-orange-500/20",
      "Technology Partner": "bg-pink-500/10 text-pink-400 border-pink-500/20",
      Other: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    };
    return colors[type] || colors["Other"];
  };

  const StatCard = ({ title, value, change, icon: Icon }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50" : "bg-white border-gray-200 hover:border-gray-300"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className={`text-[14px] font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
        >
          {title}
        </div>
        {Icon && (
          <Icon
            size={20}
            className={isDarkMode ? "text-gray-400" : "text-gray-400"}
          />
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div
            className={`text-[28px] font-bold ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
          >
            {value}
          </div>
          {change !== undefined && (
            <div
              className={`flex items-center text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
            >
              <TrendingUp className="mr-1 text-green-400" size={20} />
              {change}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const AccountRow = ({ account }) => {
    const initials = account.name
      ? account.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";

    return (
      <tr
        className={`border-b transition-colors cursor-pointer ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
        onClick={() => navigate(`/accounts/${account.id}`)}
      >
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
              {initials}
            </div>
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {account.name}
              </div>
              {account.accountNumber && (
                <div
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {account.accountNumber}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          {account.type && (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium ${getTypeColor(
                account.type,
              )}`}
            >
              {account.type}
            </span>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          {account.industry || "—"}
        </td>
        <td className="py-4 px-4">
          {account.phone ? (
            <a
              href={`tel:${account.phone}`}
              className="text-blue-400 hover:text-blue-300 transition-colors hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {account.phone}
            </a>
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              —
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {account.website ? (
            <a
              href={account.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 transition-colors hover:underline flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Globe size={14} />
              {account.website.replace(/^https?:\/\//, "").substring(0, 30)}
            </a>
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              —
            </span>
          )}
        </td>
        <td className="py-4 px-4">
          {account.owner ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
              >
                {account.owner.name.charAt(0)}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {account.owner.name}
              </span>
            </div>
          ) : (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              Unassigned
            </span>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {new Date(account.createdAt).toLocaleDateString()}
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Accounts
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Manage your customer accounts and organizations
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchAccounts}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>
            {/* <button
              onClick={() => setShowMergeManager(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
            >
              <Users size={16} />
              Find Duplicates
            </button> */}

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Account</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search accounts..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              className={`pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
            />
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
          </div>

          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
          >
            <option value="">All Types</option>
            <option value="Prospect">Prospect</option>
            <option value="Customer - Direct">Customer - Direct</option>
            <option value="Customer - Channel">Customer - Channel</option>
            <option value="Channel Partner / Reseller">
              Channel Partner / Reseller
            </option>
            <option value="Installation Partner">Installation Partner</option>
            <option value="Technology Partner">Technology Partner</option>
            <option value="Other">Other</option>
          </select>

          <select
            value={filters.industry}
            onChange={(e) =>
              setFilters({ ...filters, industry: e.target.value })
            }
            className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
          >
            <option value="">All Industries</option>
            <option value="Technology">Technology</option>
            <option value="Finance">Finance</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Retail">Retail</option>
            <option value="Other">Other</option>
          </select>

          <select
            value={filters.rating}
            onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
            className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
          >
            <option value="">All Ratings</option>
            <option value="Hot">Hot</option>
            <option value="Warm">Warm</option>
            <option value="Cold">Cold</option>
          </select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Accounts"
            value={stats.totalAccounts}
            change={15}
            icon={Building2}
          />
          <StatCard
            title="New This Month"
            value={stats.recentAccounts}
            change={8}
            icon={TrendingUp}
          />
          <StatCard
            title="Active Customers"
            value={Object.values(stats.accountsByType).reduce(
              (a, b) => a + b,
              0,
            )}
            icon={Users}
          />
          <StatCard
            title="Partners"
            value={stats.accountsByType["Channel Partner / Reseller"] || 0}
            icon={Users}
          />
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                All Accounts{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} accounts
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Manage your customer accounts and business relationships
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Account Name
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Type
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Industry
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Phone
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Website
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Owner
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Created
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading accounts...
                    </td>
                  </tr>
                ) : accounts.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No accounts found. Create your first account to get
                      started.
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <AccountRow key={account.id} account={account} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Create Account Modal */}
      {showCreateModal && (
        <CreateAccountModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
      {showMergeManager && (
        <AccountMergeManager onClose={() => setShowMergeManager(false)} />
      )}
    </div>
  );
};

export default Accounts;
